import axios from "axios";
import { environment } from "./environments/environment";
import LocalStorageKeys from "./data/localStorageKeys";

export const Api = Object.freeze({
  Banner: {
    /**
     * Elimina el banner con el id indicado
     * @param {*} token token de autorización
     * @param {*} id id del banner a eliminar
     * @returns {Promise<import("axios").AxiosResponse<Object>>} Banner eliminado
     */
    eliminar(token, id) {
      return axios.delete(environment.API_URL + "banner/" + id, {
        headers: { Authorization: "Token " + token }
      });
    },
    /**
     * Devuelve todos los banners
     * @returns {Promise<import("axios").AxiosResponse<Object[]>>} lista de banners
     */
    getTodos() {
      return axios.get(environment.API_URL + "banner");
    }
  },
  Beneficio: {
    /**
     * Cantidad de beneficios que puedan ser vistos por invitados
     * @param {object} nombre Objeto búsqueda
     * @param {number} subcategoria id de subcategoria
     * */
    cantidadInvitado({
      busqueda = null,
      idComercio = null,
      idLocalidad = null,
      subcategoria = null,
      idTipoDescuento = environment.ID_TIPO_DESCUENTO_STANDAR
    }) {
      const dto = {
        busqueda: busqueda,
        idComercio: idComercio,
        idLocalidad: idLocalidad,
        subcategoria: subcategoria,
        idTipoDescuento: idTipoDescuento
      };

      return axios.post(
        environment.API_URL + "beneficio/cantidad/invitado",
        dto
      );
    },
    /**
     * Cantidad de beneficios que puedan ser vistos por clientes
     * @param {object} busqueda objeto búsqueda
     * @param {number} subcategoria id de subcategoria
     * @param {boolean} suscripto filtrar los beneficios por sucriptos o no, no filtrar si se quieren todos
     * @param {number} idComercio comercio al que pertenecen los beneficios
     * */
    cantidadCliente(
      idCliente,
      token,
      {
        busqueda = null,
        idComercio = null,
        idLocalidad = null,
        subcategoria = null,
        suscripto = null
      }
    ) {
      const dto = {
        busqueda: busqueda,
        idComercio: idComercio,
        idLocalidad: idLocalidad,
        subcategoria: subcategoria,
        suscripto: suscripto
      };

      return axios.post(
        environment.API_URL + "beneficio/cantidad/" + idCliente,
        dto,
        { headers: { Authorization: "Token " + token } }
      );
    },
    /*
     * Beneficios que puedan ser vistos por invitados
     * @param {number} desde Inicio de página
     * @param {number} hasta Fin de página
     * @param {object} busqueda Objeto búsqueda
     * @param {number} subcategoria id de subcategoria
     * @param {idTipoDescuento} idTipoDescuento == 1 hace referencia al descuento Standar para Invitados
     * */
    getInvitado({
      desde = 1,
      hasta = 20,
      busqueda = null,
      idComercio = null,
      idLocalidad = null,
      subcategoria = null,
      idTipoDescuento = environment.ID_TIPO_DESCUENTO_STANDAR
    }) {
      const dto = {
        busqueda: busqueda,
        desde: desde,
        hasta: hasta,
        idComercio: idComercio,
        idLocalidad: idLocalidad,
        subcategoria: subcategoria,
        idTipoDescuento: idTipoDescuento
      };

      return axios.post(environment.API_URL + "beneficio/invitado", dto);
    },
    /**
     * Beneficios que puedan ser vistos por clientes
     * @param {number} desde Inicio de página
     * @param {number} hasta Fin de página
     * @param {object} busqueda Objeto búsqueda
     * @param {number} subcategoria id de subcategoria
     * @param {boolean} suscripto filtrar los beneficios por sucriptos o no, no filtrar si se quieren todos
     * @param {number} idComercio comercio al que pertenecen los beneficios
     * */
    getCliente(
      idCliente,
      token,
      {
        desde = 1,
        hasta = 20,
        busqueda = null,
        idComercio = null,
        idLocalidad = null,
        subcategoria = null,
        suscripto = null
      }
    ) {
      const dto = {
        busqueda: busqueda,
        desde: desde,
        hasta: hasta,
        idComercio: idComercio,
        idLocalidad: idLocalidad,
        subcategoria: subcategoria,
        suscripto: suscripto
      };

      return axios.post(
        environment.API_URL + "beneficio/cliente/" + idCliente,
        dto,
        { headers: { Authorization: "Token " + token } }
      );
    },
    /**
     * Ordena el beneficio
     * @param {*} token
     * @param {*} id del beneficio a ordenar
     * @param {*} delanteDeId id del beneficio al cual se adelantó
     * @returns {Promise<import("axios").AxiosResponse<Array<Object>>>} lista de cambios de prioridad con {idElemento, nuevaPrioridad}
     */
    ordenar(id, { atrasDeId = undefined, delanteDeId = undefined }) {
      const datos = {
        id: id,
        atrasDeId,
        delanteDeId
      };

      const token = localStorage.getItem(LocalStorageKeys.token);

      return axios.post(
        environment.API_URL + "beneficio/" + id + "/ordenar",
        datos,
        {
          headers: { Authorization: "Token " + token }
        }
      );
    }
  },
  Categoria: {
    get(id) {
      return axios.get(environment.API_URL + "categoria/" + id);
    },
    /**
     * Todas las categorías
     * @returns {import("axios").AxiosResponse<Array<any>>}
     */
    getTodas() {
      return axios.get(environment.API_URL + "categoria");
    },
    /**
     * Devuelve la categoría cuya subcategoría es el idSubcategoria
     * @param {*} idSubcategoria
     * @returns {Promise<import("axios").AxiosResponse<object>>} categoría
     */
    getCategoriaDeSubcategoria(idSubcategoria) {
      return axios.get(
        environment.API_URL + "categoria/bysubcategoria/" + idSubcategoria
      );
    },
    /**Todas las subcategorias de una categoria */
    getSubcategoriasDeCategoria(idCategoria) {
      return axios.get(environment.API_URL + "subcategoria/" + idCategoria);
    },
    /**Datos de una subcategoria */
    getSubcategoria(idSubcategoria) {
      return axios.get(
        environment.API_URL + "subcategoria/id/" + idSubcategoria
      );
    }
  },
  Comentario: {
    /**
     * Acepta el comentario identificado por la clave compuesta idComercio y idUsuario
     * @param {*} idComercio
     * @param {*} idUsuario
     * @param {*} token
     */
    aprobar(idComercio, idUsuario, token) {
      return axios.put(
        environment.API_URL +
          "comentario/" +
          idComercio +
          "/" +
          idUsuario +
          "/aprobar",
        // Es necesario un body para que funcione el put
        {},
        {
          headers: { Authorization: "Token " + token }
        }
      );
    },
    /**
     * Cantidad de comentarios aprobados
     * */
    cantidadAprobados(busqueda = null, idComercio = null) {
      const filtro = {
        busqueda: busqueda,
        idComercio: idComercio
      };

      return axios.post(
        environment.API_URL + "comentario/aprobado/cantidad",
        filtro
      );
    },
    /** Cantidad de comentarios pendientes */
    cantidadPendientes(token, busqueda = null, idComercio = null) {
      const filtro = {
        busqueda: busqueda,
        idComercio: idComercio
      };

      return axios.post(
        environment.API_URL + "comentario/pendiente/cantidad",
        filtro,
        { headers: { Authorization: "Token " + token } }
      );
    },
    /**
     * Elimina el comentario identificado por la clave compuesta idComercio e idUsuario
     * @param {*} idComercio
     * @param {*} idUsuario
     * @param {*} token
     */
    eliminar(idComercio, idUsuario, token) {
      return axios.delete(
        environment.API_URL + "comentario/" + idComercio + "/" + idUsuario,
        {
          headers: { Authorization: "Token " + token }
        }
      );
    },
    /**
     * Enviar comentario a guardar
     * @param {*} comentario
     */
    enviar(comentario) {
      return axios.post(environment.API_URL + "comentario", comentario);
    },
    /**
     * Comentarios aproboados
     * @param {*} token
     * @param {*} param1
     * @returns Lista paginada
     */
    getAprobados({ desde, hasta, busqueda = null, idComercio = null }) {
      const filtro = {
        desde: desde,
        hasta: hasta,
        busqueda: busqueda,
        idComercio: idComercio
      };
      return axios.post(
        environment.API_URL + "comentario/aprobado/filtro",
        filtro
      );
    },
    /**
     * Devuelve el comentario enviado por el usuario al comercio indicado
     * @param {*} idComercio
     * @param {*} idUsuario
     */
    getEnviado(idComercio, idUsuario) {
      return axios.get(
        environment.API_URL + "comentario/" + idComercio + "/" + idUsuario
      );
    },
    /**
     * Lista paginada de comentarios pendientes
     * @param {*} token
     * @param {*} param1
     */
    getPendientes(token, { desde, hasta, busqueda = null, idComercio = null }) {
      const filtro = {
        desde: desde,
        hasta: hasta,
        busqueda: busqueda,
        idComercio: idComercio
      };
      return axios.post(environment.API_URL + "comentario/pendiente/", filtro, {
        headers: { Authorization: "Token " + token }
      });
    },
    /**
     * Rechaza el comentario identificado por la clave compuesta idComercio y idUsuario
     * @param {*} idComercio
     * @param {*} idUsuario
     * @param {*} token
     */
    rechazar(idComercio, idUsuario, token) {
      return axios.put(
        environment.API_URL +
          "comentario/" +
          idComercio +
          "/" +
          idUsuario +
          "/rechazar",
        // Es necesario un body para que funcione el put
        {},
        {
          headers: { Authorization: "Token " + token }
        }
      );
    }
  },
  Comercio: {
    get(id) {
      return axios.get(environment.API_URL + "comercio/" + id);
    },
    /**
     * Cantidad de todos los comercios que existen
     * @param {object} busqueda Objeto búsqueda
     * */
    countWithFilter(token, busqueda = null) {
      const dto = {
        busqueda: busqueda
      };

      return axios.post(environment.API_URL + "comercio/cantidad/", dto, {
        headers: { Authorization: "Token " + token }
      });
    },
    /**
     * Todos los comercios que existen
     * @param {number} desde Inicio de página
     * @param {number} hasta Fin de página
     * @param {object} busqueda Objeto búsqueda
     * */
    getTodos(token, desde = 1, hasta = null, busqueda = null) {
      const dto = {
        desde: desde,
        hasta: hasta,
        busqueda: busqueda
      };
      return axios.post(environment.API_URL + "comercio/todos/", dto, {
        headers: { Authorization: "Token " + token }
      });
    }
  },
  Estadistica: {
    getAllEntity(token) {
      return axios.get(environment.API_URL + "auditoria/", {
        headers: { Authorization: "Token " + token }
      });
    },
    findAuditoriaOrderByOperacionAndVisitas(token) {
      return axios.get(environment.API_URL + "auditoria/order", {
        headers: { Authorization: "Token " + token }
      });
    },
    findAuditoriaFiltro(filtro, token) {
      return axios.post(environment.API_URL + "auditoria/filtro", filtro, {
        headers: { Authorization: "Token " + token }
      });
    }
  },
  General: {
    getLogoSB(idUsuarioSB) {
      return axios.get(environment.API_URL + "sb/logo/" + idUsuarioSB);
    }
  },
  Localidad: {
    /** Devuelve la localidad según el código postal detectado de la ip actual,
     *  usar con discreción, ya que se llama a una API externa con límite de peticiones
     */
    getByIp() {
      return axios.get(environment.API_URL + "localidad/ip");
    },
    getLocalidadesDeProvincia(idProvincia) {
      return axios.get(
        environment.API_URL + "localidad/provincia/" + idProvincia
      );
    },
    getProvincias() {
      return axios.get(environment.API_URL + "provincia");
    },
    /**
     * Devuelve la provincia que contiene a la localidad con el id indicado
     * @param {string} idLocalidad Id de la localidad de la cual se buscará la provincia
     */
    getProvinciaDeLocalidad(idLocalidad) {
      return axios.get(
        environment.API_URL + "provincia/localidad/" + idLocalidad
      );
    }
  },
  Logos: {
    subir(idComercio, logo, token) {
      return axios.post(
        environment.API_URL + "uploads/logos/" + idComercio,
        logo,
        {
          headers: {
            Authorization: "Token " + token,
            "Content-Type": "multipart/form-data"
          }
        }
      );
    }
  },
  Mensaje: {
    guardar(mensaje, token) {
      return axios.put(environment.API_URL + "mensaje/" + mensaje.id, mensaje, {
        headers: { Authorization: "Token " + token }
      });
    }
  },
  Solicitud: {
    /**
     * Enviar solicitud
     * @param {*} solicitud
     * @returns {Promise<import("axios").AxiosResponse<number>>} id del comercio provisorio creado
     */
    enviar(solicitud) {
      return axios.post(environment.API_URL + "solicitud/", solicitud);
    }
  },
  Suscripcion: {
    eliminar(suscripcion, token) {
      return axios.post(
        environment.API_URL + "sub-beneficio/deleteSuscripcion",
        suscripcion,
        {
          headers: { Authorization: "Token " + token }
        }
      );
    },
    /**
     * Listado de beneficios a los que está suscripto el cliente
     * @param {*} dni
     * @param {*} token
     */
    getCliente(dni, token) {
      return axios.get(environment.API_URL + "sub-beneficio/cliente/" + dni, {
        headers: { Authorization: "Token " + token }
      });
    },
    /**
     * Ordena las suscripciones para un cliente
     * @param {Array<Number>} listaOrdenada Lista de ids de beneficios ordenada
     * @param {String} dni DNI del cliente suscripto a esos beneficios
     * @param {String} token
     */
    ordenarCliente(listaOrdenada, dni, token) {
      return axios.post(
        environment.API_URL + "sub-beneficio/ordenar/" + dni,
        listaOrdenada,
        {
          headers: { Authorization: "Token " + token }
        }
      );
    }
  },
});
